import { useQuery } from "@tanstack/react-query";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { InvestorSidebar } from "@/components/dashboard/sidebar";
import { StatementList } from "@/components/dashboard/statement-list";
import { ThemeToggle } from "@/components/theme-toggle";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { FileText, Download, Loader2 } from "lucide-react";
import type { Statement } from "@shared/schema";
import { API_BASE_URL } from "@/lib/queryClient";

interface Agreement {
  _id?: string;
  id?: string;
  title: string;
  fileName: string;
  createdAt: string;
}

export default function StatementsPage() {
  const { data: statements, isLoading } = useQuery<Statement[]>({
    queryKey: ["/api/investor/statements"],
  });

  const { data: agreements, isLoading: loadingAgreements } = useQuery<Agreement[]>({
    queryKey: ["/api/investor/agreements"],
  });

  const displayStatements = statements || [];

  const monthlyStatements = displayStatements.filter((s) => s.type === "monthly");
  const quarterlyStatements = displayStatements.filter((s) => s.type === "quarterly");
  const annualStatements = displayStatements.filter((s) => s.type === "annual");

  const sidebarStyle = {
    "--sidebar-width": "17rem",
    "--sidebar-width-icon": "3.5rem",
  };

  return (
    <SidebarProvider style={sidebarStyle as React.CSSProperties}>
      <div className="flex h-screen w-full overflow-hidden">
        <InvestorSidebar />
        <div className="flex flex-col flex-1 h-full overflow-hidden">
          <header className="flex items-center justify-between gap-4 h-16 px-6 border-b bg-background/95 backdrop-blur shrink-0">
            <div className="flex items-center gap-4">
              <SidebarTrigger data-testid="button-sidebar-toggle" />
              <div>
                <h1 className="text-lg font-semibold">Statements & Reports</h1>
                <p className="text-sm text-muted-foreground">Download your investment statements and signed agreements</p>
              </div>
            </div>
            <ThemeToggle />
          </header>

          <main className="flex-1 p-6 overflow-y-auto">
            <Tabs defaultValue="all" className="space-y-6">
              <TabsList>
                <TabsTrigger value="all" data-testid="tab-all-statements">
                  All Statements
                </TabsTrigger>
                <TabsTrigger value="monthly" data-testid="tab-monthly-statements">
                  Monthly
                </TabsTrigger>
                <TabsTrigger value="quarterly" data-testid="tab-quarterly-statements">
                  Quarterly
                </TabsTrigger>
                <TabsTrigger value="annual" data-testid="tab-annual-statements">
                  Annual
                </TabsTrigger>
                <TabsTrigger value="agreements" data-testid="tab-agreements">
                  Agreements & Deleted Contracts
                </TabsTrigger>
              </TabsList>

              <TabsContent value="all">
                <StatementList statements={displayStatements} isLoading={isLoading} />
              </TabsContent>

              <TabsContent value="monthly">
                <StatementList statements={monthlyStatements} isLoading={isLoading} />
              </TabsContent>

              <TabsContent value="quarterly">
                <StatementList statements={quarterlyStatements} isLoading={isLoading} />
              </TabsContent>

              <TabsContent value="annual">
                <StatementList statements={annualStatements} isLoading={isLoading} />
              </TabsContent>

              <TabsContent value="agreements">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg font-semibold">Agreements & Deleted Contracts</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {loadingAgreements ? (
                      <div className="flex justify-center py-10">
                        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
                      </div>
                    ) : agreements && agreements.length > 0 ? (
                      <div className="space-y-3">
                        {agreements.map((ag) => {
                          const agId = ag._id || ag.id;
                          return (
                            <div
                              key={agId}
                              className="flex items-center justify-between p-4 rounded-xl border bg-card hover:bg-muted/30 transition-colors"
                            >
                              <div className="flex items-center gap-4">
                                <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center text-accent">
                                  <FileText className="w-5 h-5" />
                                </div>
                                <div>
                                  <p className="font-semibold text-sm">{ag.title}</p>
                                  <p className="text-xs text-muted-foreground">
                                    {ag.fileName} • Added {new Date(ag.createdAt).toLocaleDateString("en-IN")}
                                  </p>
                                </div>
                              </div>
                              <a
                                href={`${API_BASE_URL}/api/agreements/${agId}/download`}
                                target="_blank"
                                rel="noreferrer"
                              >
                                <Button size="sm" className="gap-2">
                                  <Download className="w-4 h-4" />
                                  Download PDF
                                </Button>
                              </a>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="flex flex-col items-center justify-center py-12 text-center">
                        <FileText className="w-12 h-12 text-muted-foreground/20 mb-3" />
                        <p className="font-medium text-muted-foreground">No agreements found</p>
                        <p className="text-sm text-muted-foreground/50">
                          Your investment agreement documents uploaded by the management team will appear here.
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </main>
        </div>
      </div>
    </SidebarProvider>
  );
}
